import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import MobileFooter from '../../../components/MobileFooter';
import { PATHS } from '../../../routes/paths';
import { convertToVwMob } from '../../../Utils/responsive';
import { extractDigits, formatCurrencyInput, formatKoreanCurrency, parseCurrencyNumber } from '../../../Utils/formatCurrency';
import pictureIconImg from '../../../assets/Icon/project/picture_icon.png';
import checkIconImg from '../../../assets/Icon/project/check_icon.png';
import kakaoIconImg from '../../../assets/Icon/project/kakao_icon.png';
import successCheckLottie from '../../../assets/Icon/project/Lottie/success_check.lottie';
import RichTextEditor, { type RichTextEditorHandle } from '../../../components/common/RichTextEditor';
import DSModal from '../../../components/designSystem/DSModal';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { checkProjectId, createProject, getProject, uploadProjectImage } from '../../../Module/Project/Presentation/projectActions';
import { selectSelectedProject } from '../../../Module/Project/Presentation/projectSelectors';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../../Module/User/Presentation/userSelectors';
import { theme } from '../../../styles/theme';
import logoImg from '../../../assets/Logo/main_logo.png';
import { useTranslation } from 'react-i18next';
import { sanitizeProjectDescHtml } from '../../../Utils/projectDesc';
import { openMailClient } from '../../../Utils/mail';
import { buildProjectShareMailTemplate } from '../../../Utils/shareMailTemplates';
import { getProjectShareImageUrl, shareKakaoFeed } from '../../../Utils/kakaoShare';
import { formatUserFullName } from '../../../Utils/formatUserName';
import { isIndividualUserType } from '../../../Utils/userType';
import { getYouTubeThumbnail } from '../../../Utils/youtube';
import { isImageSizeAllowed } from '../../../Utils/imageUpload';
import PreviewGeneralMob from './components/PreviewGeneralMob';
import PreviewPartnerMob from './components/PreviewPartnerMob';

const stripEllipsis = (value: string) => value.replace(/\.+$/, '');
const sanitizeProjectTitle = (value: string) => {
  return value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ');
};

type FundingType = 'regular' | 'once';
type Currency = 'KRW' | 'USD';
type MediaType = 'image' | 'video';
type Membership = 'partner' | 'builder' | 'prime' | 'general';
type ProjectIdCheckState = 'idle' | 'checking' | 'available' | 'taken' | 'error';
const PROJECT_ID_REGEX = /^(?=.*[a-zA-Z])[a-zA-Z0-9]{4,20}$/;
const sanitizeProjectIdInput = (value: string) => value.replace(/[^a-zA-Z0-9]/g, '');
const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
const KAKAO_SDK_SRC = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';

const FOCUSED_TOP_H = 56;
const KEYBOARD_BAR_H = 80;
const KEYBOARD_EXTRA_SCROLL_PAD = 170;
const KEYBOARD_SCROLL_PAD = KEYBOARD_BAR_H + KEYBOARD_EXTRA_SCROLL_PAD;
const STORY_EDITOR_BOTTOM_SPACER_PX = 200;

const NewProjectMob = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation');
  const formatMembershipBenefitMob = useCallback(
    (key: string) => t(`${key}Mobile` as never, { defaultValue: t(key as never) }),
    [t],
  );
  const creatingLabel = stripEllipsis(t('page.newProject.actions.creating'));
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);
  const selectedProject = useAppSelector(selectSelectedProject);
  const footerRef = useRef<HTMLElement | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<RichTextEditorHandle | null>(null);
  const prevStepRef = useRef<1 | 2 | 3 | 4 | 5 | null>(null);
  const lastPickerOpenRef = useRef(0);
  const keyboardStickyRef = useRef<HTMLDivElement>(null);
  const prevStoryEmbeddedCountRef = useRef(0);

  const [showSticky, setShowSticky] = useState(true);
  const [isStoryFocused, setIsStoryFocused] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isStoryFocused || !window.visualViewport) {
      setIsKeyboardOpen(false);
      return;
    }

    const handleVisualViewportChange = () => {
      if (!keyboardStickyRef.current || !window.visualViewport) return;
      
      const visualViewport = window.visualViewport;
      const layoutHeight = document.documentElement.clientHeight;
      const visualHeight = visualViewport.height;
      const visualTop = visualViewport.offsetTop;

      const offset = Math.max(0, layoutHeight - visualHeight - visualTop);
      const opened = offset > 60;
      setIsKeyboardOpen(opened);
      keyboardStickyRef.current.style.bottom = `${offset}px`;
    };

    window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    window.visualViewport.addEventListener('scroll', handleVisualViewportChange);
    
    handleVisualViewportChange();

    return () => {
      window.visualViewport?.removeEventListener('resize', handleVisualViewportChange);
      window.visualViewport?.removeEventListener('scroll', handleVisualViewportChange);
      if (keyboardStickyRef.current) {
        keyboardStickyRef.current.style.bottom = '0px';
      }
      setIsKeyboardOpen(false);
    };
  }, [isStoryFocused]);

  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  const liftStoryScroll = useCallback((amount: number) => {
    if (step !== 3) return;
    window.requestAnimationFrame(() => {
      mainRef.current?.scrollBy({ top: amount, behavior: 'auto' });
    });
  }, [step]);

  const scrollStep3EditorIntoView = useCallback(() => {
    const scrollEl = mainRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;

    const pad = 48;
    const scrollRect = scrollEl.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();

    const sel = window.getSelection();
    let focusTop = editorRect.top;
    let focusBottom = editorRect.bottom;
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const r = range.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) {
        focusTop = r.top;
        focusBottom = r.bottom;
      }
    }

    if (focusBottom > scrollRect.bottom - pad) {
      scrollEl.scrollTop += focusBottom - (scrollRect.bottom - pad);
    } else if (focusTop < scrollRect.top + pad) {
      scrollEl.scrollTop += focusTop - (scrollRect.top + pad);
    }
  }, []);

  const scrollStep3TailIntoView = useCallback(() => {
    const scrollEl = mainRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;
    const images = editorEl.querySelectorAll<HTMLElement>('figure[data-embed="story-image"]');
    const lastImage = images.length ? images[images.length - 1] : null;
    const anchorRect = lastImage?.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    const visibleBottom = scrollRect.bottom - 156;
    const targetBottom = Math.max(anchorRect ? anchorRect.bottom + 36 : 0, editorRect.bottom);
    if (targetBottom > visibleBottom) {
      scrollEl.scrollTop += targetBottom - visibleBottom;
    }
  }, []);

  const focusStep3Editor = useCallback(() => {
    setIsStoryFocused(true);
    const timeoutIds: number[] = [];
    const rafIds: number[] = [];
    const run = () => {
      editorRef.current?.focusAtEnd();
      const rid = requestAnimationFrame(() => {
        scrollStep3EditorIntoView();
        scrollStep3TailIntoView();
        liftStoryScroll(220);
      });
      rafIds.push(rid);
    };

    [0, 90, 200].forEach((delay) => {
      const tid = window.setTimeout(() => {
        const rid = requestAnimationFrame(run);
        rafIds.push(rid);
      }, delay);
      timeoutIds.push(tid);
    });

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      rafIds.forEach((id) => cancelAnimationFrame(id));
    };
  }, [liftStoryScroll, scrollStep3EditorIntoView, scrollStep3TailIntoView]);

  useEffect(() => {
    if (step !== 3 && isStoryFocused) {
      setIsStoryFocused(false);
      setIsKeyboardOpen(false);
    }
  }, [isStoryFocused, step]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      mainRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    });
  }, [step]);

  useEffect(() => {
    if (step === 3 && prevStepRef.current !== 3) {
      prevStepRef.current = 3;
      return focusStep3Editor();
    }
    prevStepRef.current = step;
    return undefined;
  }, [step, focusStep3Editor]);

  useLayoutEffect(() => {
    if (step !== 3) return;
    setIsStoryFocused(true);
    editorRef.current?.focusAtEnd();
  }, [step]);

  useEffect(() => {
    if (step !== 3 || !isStoryFocused) return;
    const t = window.setTimeout(() => {
      editorRef.current?.focus();
      scrollStep3EditorIntoView();
      scrollStep3TailIntoView();
    }, 40);
    return () => window.clearTimeout(t);
  }, [step, isStoryFocused, scrollStep3EditorIntoView, scrollStep3TailIntoView]);

  // step1
  const [fundingType, setFundingType] = useState<FundingType>('regular');
  const [currency, setCurrency] = useState<Currency>('KRW');
  const [goalAmount, setGoalAmount] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [projectIdCheckState, setProjectIdCheckState] = useState<ProjectIdCheckState>('idle');
  const [checkedProjectId, setCheckedProjectId] = useState<string>('');

  // step2
  const [mediaType, setMediaType] = useState<MediaType>('image');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoUrlFocused, setVideoUrlFocused] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const projectIdCheckTimerRef = useRef<number | null>(null);
  const projectIdCheckRequestSeqRef = useRef(0);

  // step3
  const [storyHtml, setStoryHtml] = useState<string>('');

  // step4
  const [membership, setMembership] = useState<Membership>('prime');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState<string>('');
  const [isStep5StoryExpanded, setIsStep5StoryExpanded] = useState(false);
  const kakaoSdkLoadingRef = useRef<Promise<any> | null>(null);

  useEffect(() => {
    if (step !== 5) return;
    const hasMedia = Boolean(storyHtml && /data-embed=['"](story-image|pinned-image)['"]|<img/i.test(storyHtml));
    setIsStep5StoryExpanded(hasMedia);
  }, [step, storyHtml]);

  useEffect(() => {
    if (step === 3) return;
    const el = footerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        setShowSticky(!visible);
      },
      { threshold: [0, 0.2, 0.6] },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [step]);

  useEffect(() => {
    if (!mediaFile) {
      setMediaPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(mediaFile);
    setMediaPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaFile]);

  const titleTrimmed = sanitizeProjectTitle(title).trim().slice(0, 40);
  const projectIdTrimmed = projectId.trim();
  const isProjectIdFormatValid = PROJECT_ID_REGEX.test(projectIdTrimmed);
  const isProjectIdAvailable = projectIdTrimmed.length > 0 && checkedProjectId === projectIdTrimmed && projectIdCheckState === 'available';
  const isProjectIdTaken = projectIdTrimmed.length > 0 && checkedProjectId === projectIdTrimmed && projectIdCheckState === 'taken';
  const userId =
    (session as { user?: { id?: string } } | undefined)?.user?.id ?? window.localStorage.getItem('missionfund.userId') ?? '';
  const profileFullName = formatUserFullName(userProfile?.first_name, userProfile?.last_name);
  const snsName =
    typeof userProfile?.sns_name === 'string' ? userProfile.sns_name.trim() : '';
  const sessionFallbackFullName = formatUserFullName(
    (session as { user?: { first_name?: string } } | undefined)?.user?.first_name,
    (session as { user?: { last_name?: string } } | undefined)?.user?.last_name,
  );
  const userNameFallback =
    (profileFullName ? profileFullName : undefined) ??
    (snsName ? snsName : undefined) ??
    (session as { user?: { name?: string } } | undefined)?.user?.name ??
    (sessionFallbackFullName ? sessionFallbackFullName : undefined) ??
    (session as { user?: { email?: string } } | undefined)?.user?.email ??
    '';
  const organizationName = typeof userProfile?.organization === 'string' ? userProfile.organization.trim() : '';
  const restrictedRegion = userProfile?.restricted_region === true || String(userProfile?.restricted_region) === 'true';
  const isIndividual = isIndividualUserType(userProfile?.user_type);
  const userFullName = restrictedRegion
    ? t('page.newProject.preview.securityCountryName')
    : isIndividual
      ? userNameFallback
      : (organizationName || userNameFallback);
  const restrictedRegionStoredFullName = formatUserFullName(userProfile?.first_name, userProfile?.last_name).trim();
  const projectUserFullName = restrictedRegion
    ? restrictedRegionStoredFullName || 'user'
    : userFullName || 'user';
  const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const showGoalPreview = currency === 'KRW' && numericGoal >= 10000;
  const goalPreviewText = showGoalPreview ? formatKoreanCurrency(numericGoal) : '';

  const hasRepresentativeImage = useMemo(() => {
    return mediaType === 'image' && Boolean(mediaPreviewUrl);
  }, [mediaPreviewUrl, mediaType]);

  const storyEmbeddedCount = useMemo(() => {
    if (!storyHtml) return 0;
    const story = storyHtml.match(/data-embed="story-image"/g) ?? [];
    return story.length;
  }, [storyHtml]);
  const hasStoryContent = useMemo(() => {
    if (!storyHtml) return false;
    const plain = storyHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return plain.length > 0;
  }, [storyHtml]);

  const totalPhotoCount = useMemo(() => {
    return storyEmbeddedCount;
  }, [storyEmbeddedCount]);

  useEffect(() => {
    if (step !== 3) {
      prevStoryEmbeddedCountRef.current = totalPhotoCount;
      return;
    }
    if (totalPhotoCount > prevStoryEmbeddedCountRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsStoryFocused(true);
          editorRef.current?.focus();
          scrollStep3EditorIntoView();
          scrollStep3TailIntoView();
                      liftStoryScroll(220);
        });
      });
    }
    prevStoryEmbeddedCountRef.current = totalPhotoCount;
  }, [step, totalPhotoCount, scrollStep3EditorIntoView, scrollStep3TailIntoView, liftStoryScroll]);
  const hasDraftChanges = useMemo(() => {
    if (completeOpen) return false;
    return (
      step > 1
      || fundingType !== 'regular'
      || currency !== 'KRW'
      || Boolean(goalAmount.trim())
      || Boolean(titleTrimmed)
      || Boolean(projectIdTrimmed)
      || mediaType !== 'image'
      || Boolean(mediaFile)
      || Boolean(mediaPreviewUrl)
      || Boolean(videoUrl.trim())
      || hasStoryContent
      || membership !== 'partner'
      || previewOpen
    );
  }, [
    completeOpen,
    step,
    fundingType,
    currency,
    goalAmount,
    titleTrimmed,
    projectIdTrimmed,
    mediaType,
    mediaFile,
    mediaPreviewUrl,
    videoUrl,
    hasStoryContent,
    membership,
    previewOpen,
  ]);

  useEffect(() => {
    if (!hasDraftChanges) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasDraftChanges]);

  const projectIdStatusMessage = useMemo(() => {
    if (!projectIdTrimmed || !isProjectIdFormatValid) return '';
    if (projectIdCheckState === 'checking') return t('page.newProject.validation.projectId.checking');
    if (isProjectIdTaken) return t('page.newProject.validation.projectId.taken');
    if (isProjectIdAvailable) return t('page.newProject.validation.projectId.available');
    if (projectIdCheckState === 'error') return t('page.newProject.validation.projectId.error');
    return '';
  }, [isProjectIdAvailable, isProjectIdFormatValid, isProjectIdTaken, projectIdCheckState, projectIdTrimmed, t]);

  const projectIdStatusColor = useMemo(() => {
    if (projectIdCheckState === 'checking') return '#357B89';
    if (isProjectIdAvailable) return '#0A8A3A';
    if (isProjectIdTaken || projectIdCheckState === 'error') return '#FF0000';
    return '#357B89';
  }, [isProjectIdAvailable, isProjectIdTaken, projectIdCheckState]);

  const projectIdHintStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (projectIdTrimmed.length > 0 && !isProjectIdFormatValid) {
      return { color: '#FF0000' };
    }
    if (projectIdStatusMessage) {
      return { color: projectIdStatusColor };
    }
    return undefined;
  }, [isProjectIdFormatValid, projectIdStatusColor, projectIdStatusMessage, projectIdTrimmed.length]);

  const runProjectIdCheck = useCallback(
    async (targetProjectId: string) => {
      const id = targetProjectId.trim();
      if (!PROJECT_ID_REGEX.test(id)) return;
      if (
        checkedProjectId === id &&
        (projectIdCheckState === 'available' || projectIdCheckState === 'taken' || projectIdCheckState === 'checking')
      ) {
        return;
      }

      const requestSeq = projectIdCheckRequestSeqRef.current + 1;
      projectIdCheckRequestSeqRef.current = requestSeq;
      setCheckedProjectId(id);
      setProjectIdCheckState('checking');

      try {
        const response = await dispatch(checkProjectId({ project_id: id })).unwrap();
        if (projectIdCheckRequestSeqRef.current !== requestSeq) return;
        const resultCode = Number((response as any)?.result_code ?? (response as any)?.result_Code ?? -1);
        if (resultCode === 0) {
          setProjectIdCheckState('available');
          return;
        }
        if (resultCode === 63001) {
          setProjectIdCheckState('taken');
          return;
        }
        setProjectIdCheckState('error');
      } catch {
        if (projectIdCheckRequestSeqRef.current !== requestSeq) return;
        setProjectIdCheckState('error');
      }
    },
    [checkedProjectId, dispatch, projectIdCheckState],
  );

  const scheduleProjectIdCheck = useCallback(
    (targetProjectId: string) => {
      if (projectIdCheckTimerRef.current !== null) {
        window.clearTimeout(projectIdCheckTimerRef.current);
      }
      projectIdCheckTimerRef.current = window.setTimeout(() => {
        void runProjectIdCheck(targetProjectId);
      }, 300);
    },
    [runProjectIdCheck],
  );

  const handleTitleFieldFocus = useCallback(() => {
    if (!isProjectIdFormatValid) return;
    scheduleProjectIdCheck(projectIdTrimmed);
  }, [isProjectIdFormatValid, projectIdTrimmed, scheduleProjectIdCheck]);

  const handleProjectIdFieldBlur = useCallback(() => {
    if (!isProjectIdFormatValid) return;
    void runProjectIdCheck(projectIdTrimmed);
  }, [isProjectIdFormatValid, projectIdTrimmed, runProjectIdCheck]);

  useEffect(() => {
    if (projectIdCheckTimerRef.current !== null && checkedProjectId !== projectIdTrimmed) {
      window.clearTimeout(projectIdCheckTimerRef.current);
      projectIdCheckTimerRef.current = null;
    }
    if (!projectIdTrimmed) {
      setProjectIdCheckState('idle');
      setCheckedProjectId('');
      return;
    }
    if (checkedProjectId && checkedProjectId !== projectIdTrimmed) {
      setProjectIdCheckState('idle');
      setCheckedProjectId('');
    }
  }, [checkedProjectId, projectIdTrimmed]);

  useEffect(() => {
    return () => {
      if (projectIdCheckTimerRef.current !== null) {
        window.clearTimeout(projectIdCheckTimerRef.current);
      }
    };
  }, []);

  const projectUrl = useMemo(() => {
    const id = (createdProjectId || projectIdTrimmed || 'yourID').trim();
    return `https://go.MissionFund.org/${id}`;
  }, [createdProjectId, projectIdTrimmed]);

  const membershipMissionAccountNo = useMemo(() => {
    if (membership === 'partner') return 'PARTNER';
    if (membership === 'builder') return 'Builder';
    if (membership === 'prime') return 'Prime';
    return '';
  }, [membership]);

  const step1Valid = useMemo(() => {
    const titleOk = titleTrimmed.length >= 4 && titleTrimmed.length <= 40;
    const idOk = isProjectIdFormatValid && isProjectIdAvailable;
    const minGoal = currency === 'KRW' ? 35000 : 35;
    const goalOk = numericGoal >= minGoal;
    return titleOk && idOk && goalOk;
  }, [currency, isProjectIdAvailable, isProjectIdFormatValid, numericGoal, titleTrimmed.length]);

  const videoUrlTrimmed = videoUrl.trim();
  const videoUrlValid = useMemo(() => {
    if (!videoUrlTrimmed) return false;
    try {
      const url = new URL(videoUrlTrimmed);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }, [videoUrlTrimmed]);
  const showVideoUrlError = Boolean(videoUrlTrimmed) && !videoUrlValid;
  const videoEmbedUrl = useMemo(() => {
    if (!videoUrlValid) return '';
    try {
      const url = new URL(videoUrlTrimmed);
      const host = url.hostname.replace('www.', '');
      
      if (host === 'youtu.be') {
        const id = url.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (host.includes('youtube.com')) {
        if (url.pathname.startsWith('/embed/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        if (url.pathname.startsWith('/shorts/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      return '';
    } catch {
      return '';
    }
  }, [videoUrlTrimmed, videoUrlValid]);
  const videoPreviewSrc = useMemo(() => {
    if (!videoUrlValid) return '';
    return videoEmbedUrl || videoUrlTrimmed;
  }, [videoEmbedUrl, videoUrlTrimmed, videoUrlValid]);

  const canNext = useMemo(() => {
    if (step === 1) return step1Valid;
    if (step === 2) return mediaType === 'image' ? Boolean(mediaFile) : videoUrlValid;
    if (step === 3) return true;
    if (step === 4) return Boolean(membership);
    return true;
  }, [mediaFile, mediaType, membership, step, step1Valid, videoUrlValid]);

  const step5StoryPlainLength = useMemo(() => {
    if (!storyHtml) return 0;
    return storyHtml
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, '')
      .length;
  }, [storyHtml]);

  const step5StoryLineCount = useMemo(() => {
    if (!storyHtml) return 0;
    const normalized = storyHtml
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\r/g, '');

    return normalized
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .length;
  }, [storyHtml]);

  const step5HasEmbeddedMedia = useMemo(() => {
    if (!storyHtml) return false;
    return /data-embed=['"](story-image|pinned-image)['"]|<img/i.test(storyHtml);
  }, [storyHtml]);

  const shouldShowStep5StoryToggle = useMemo(() => {
    if (!storyHtml) return false;
    return step5HasEmbeddedMedia || step5StoryPlainLength > 220 || step5StoryLineCount > 7;
  }, [storyHtml, step5HasEmbeddedMedia, step5StoryPlainLength, step5StoryLineCount]);

  const ensureKakaoSdk = useCallback(async () => {
    const kakaoWindow = window as any;

    if (kakaoWindow.Kakao) {
      if (!kakaoWindow.Kakao.isInitialized()) {
        kakaoWindow.Kakao.init(KAKAO_KEY);
      }
      return kakaoWindow.Kakao;
    }

    if (kakaoSdkLoadingRef.current) {
      return kakaoSdkLoadingRef.current;
    }

    kakaoSdkLoadingRef.current = new Promise((resolve, reject) => {
      const finalize = () => {
        if (!kakaoWindow.Kakao) {
          reject(new Error('Kakao SDK not loaded'));
          return;
        }
        if (!kakaoWindow.Kakao.isInitialized()) {
          kakaoWindow.Kakao.init(KAKAO_KEY);
        }
        resolve(kakaoWindow.Kakao);
      };

      const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${KAKAO_SDK_SRC}"]`);
      if (existingScript) {
        if (kakaoWindow.Kakao) {
          finalize();
          return;
        }
        existingScript.addEventListener('load', finalize, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Kakao SDK')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = KAKAO_SDK_SRC;
      script.crossOrigin = 'anonymous';
      script.addEventListener('load', finalize, { once: true });
      script.addEventListener('error', () => reject(new Error('Failed to load Kakao SDK')), { once: true });
      document.head.appendChild(script);
    }).catch((error) => {
      kakaoSdkLoadingRef.current = null;
      throw error;
    });

    return kakaoSdkLoadingRef.current;
  }, []);

  useEffect(() => {
    void ensureKakaoSdk().catch(() => {});
  }, [ensureKakaoSdk]);

  const goBack = () => setStep((s) => (s > 1 ? ((s - 1) as any) : s));
  const goNext = () => setStep((s) => (s < 5 ? ((s + 1) as any) : s));

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error(t('page.newProject.errors.imageReadFailed')));
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.readAsDataURL(file);
    });
  };

  const handleEditorImageUpload = (file: File): Promise<string> => {
    if (!isImageSizeAllowed(file)) {
      alert(t('common.errors.imageTooLarge'));
      return Promise.reject(new Error('image too large'));
    }
    return Promise.resolve(URL.createObjectURL(file));
  };

  const handleShareLink = () => {
    setIsShareModalOpen(true);
    void ensureKakaoSdk().catch(() => {}); // SDK 미리 로드 (모바일 사용자 제스처 보존용)
  };

  const runKakaoShare = async () => {
    try {
      const kakao = await ensureKakaoSdk();
      shareToKakao(kakao);
    } catch (error) {
      console.error('Failed to run Kakao share:', error);
      // eslint-disable-next-line no-alert
      alert(t('common.messages.kakaoShareLoadFailed'));
    }
  };

  const shareToKakao = (kakao: any) => {
    const restrictedRegion = userProfile?.restricted_region === true || String(userProfile?.restricted_region) === 'true';
    const pid = (createdProjectId || projectIdTrimmed).trim();
    const fromStore =
      selectedProject?.project_id === pid
        ? { image_url: selectedProject.image_url, video_url: selectedProject.video_url }
        : undefined;
    const imageUrl = getProjectShareImageUrl(
      fromStore ?? (mediaType === 'video' ? { video_url: videoUrlTrimmed } : undefined),
    );
    shareKakaoFeed(kakao, {
      url: projectUrl,
      title: titleTrimmed || t('common.shareModal.defaultTitle'),
      imageUrl,
      buttonTitle: t('common.shareModal.buttonDetail'),
      fundraiserName: userFullName || t('common.share.fundraiserFallback'),
      restrictedRegion,
    });
  };

  const runEmailShare = () => {
    const { subject, body } = buildProjectShareMailTemplate({
      fundraiserName: userFullName,
      projectName: titleTrimmed,
      projectUrl,
      defaultProjectName: t('common.shareModal.defaultTitle'),
    });
    openMailClient({ subject, body });
  };

  const handleKakaoShare = () => {
    setIsShareModalOpen(false);
    runKakaoShare(); // 사용자 클릭 직후 즉시 실행 (모바일 제스처 보존)
  };

  const handleEmailShare = () => {
    setIsShareModalOpen(false);
    runEmailShare();
  };

  const handleCopyShareLink = async () => {
    const fallbackCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = projectUrl;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(ta);
      return copied;
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(projectUrl);
        alert(t('common.messages.linkCopied'));
        return;
      }
    } catch {
      // fallback below
    }

    if (fallbackCopy()) {
      alert(t('common.messages.linkCopied'));
      return;
    }

    alert(t('page.newProject.errors.linkCopyFailed'));
  };

  const handleOpenPage = () => {
    window.open(projectUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCreate = async () => {
    if (creating) return;
    if (!userId) {
      // eslint-disable-next-line no-alert
      alert(t('page.newProject.errors.loginRequired'));
      navigate(PATHS.LOGIN);
      return;
    }
    if (!projectIdTrimmed || !titleTrimmed) {
      // eslint-disable-next-line no-alert
      alert(t('page.newProject.errors.missingProjectInfo'));
      return;
    }
    setCreating(true);
    try {
      // 1. Parse storyHtml for any local (blob:) images and upload them
      let updatedStoryHtml = storyHtml;
      const blobUrls = updatedStoryHtml.match(/src="blob:[^"]+"/g) || [];

      const partner = String((process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF');

      for (const match of blobUrls) {
        const url = match.slice(5, -1);
        try {
          const resBlob = await fetch(url);
          const blob = await resBlob.blob();
          const dataUrl = await readFileAsBase64(new File([blob], 'image.png', { type: blob.type }));
          const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;

          const uploadRes = await dispatch(
            uploadProjectImage({
              partner,
              project_id: projectIdTrimmed,
              projectpost_id: '',
              language_code: 'ko',
              isuse: true,
              image: base64,
            }),
          ).unwrap();

          if (uploadRes.image_url) {
            updatedStoryHtml = updatedStoryHtml.replace(url, uploadRes.image_url);
          }
        } catch (uploadErr) {
          console.error('Additional image upload failed:', uploadErr);
          // continue anyway
        }
      }

      updatedStoryHtml = sanitizeProjectDescHtml(updatedStoryHtml);

      // 2. Representative image (base64 string without prefix)
      let imageBase64 = '';
      if (mediaType === 'image' && mediaFile) {
        const dataUrl = await readFileAsBase64(mediaFile);
        imageBase64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      }

      const type = fundingType === 'once' ? 'OT' : 'RT';
      const youtubeThumbnail = mediaType === 'video' ? getYouTubeThumbnail(videoUrlTrimmed) ?? '' : '';

      let membershipValue = 'Basic';
      let supportPercent = '0';

      if (membership === 'partner') {
        membershipValue = 'Partner';
        supportPercent = '3';
      } else if (membership === 'builder') {
        membershipValue = 'Builder';
        supportPercent = '5';
      } else if (membership === 'prime') {
        membershipValue = 'Prime';
        supportPercent = '10';
      }

      const res = await dispatch(
        createProject({
          project_id: projectIdTrimmed,
          partner,
          title: titleTrimmed,
          project_desc: updatedStoryHtml || t('page.newProject.placeholders.projectDesc'),
          is_image_display: mediaType !== 'video',
          image: imageBase64,
          image_url: mediaType === 'video' ? youtubeThumbnail : '',
          video_url: mediaType === 'video' ? videoUrlTrimmed : '',
          user_id: userId,
          user_full_name: projectUserFullName,
          type,
          start_date: new Date().toISOString().slice(0, 10),
          end_date: '',
          goal_amount: String(numericGoal || 0),
          currency_type: currency,
          is_recommended: false,
          recommended_organization_name: '',
          is_support: false,
          support_organization_name: '',
          status: 'AC',
          comment: '',
          campaign_code: '',
          category_code: '',
          group_account_status: '',
          is_verified: false,
          mission_account_no: membershipMissionAccountNo,
          membership: membershipValue,
          support_project: {
            project_id: 'wmf',
            image_url: 'https://mfimage.s3.amazonaws.com/OMF/projects/6926946aa11b995e56a08516_1764136042390.png',
            project_desc: t('page.newProject.sample.story'),
            support_orgnaization: '',
            support_parnter: 'OMF',
            support_percent: supportPercent,
            title: t('page.newProject.sample.title'),
            user_full_name: 'Drone Team',
            user_id: '10B896ED',
            video_url: '',
          },
        }),
      ).unwrap();

      const insertedId = (res as any)?.result_message?.insertedId;
      const finalProjectId = typeof insertedId === 'string' && insertedId.trim() ? insertedId : projectIdTrimmed;
      setCreatedProjectId(finalProjectId);

      try {
        await dispatch(getProject({ project_id: finalProjectId })).unwrap();
      } catch {
        // 공유 썸네일은 폴백·유튜브 썸네일로 동작
      }

      setCompleteOpen(true);
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      alert(e?.message || (typeof e === 'string' ? e : t('page.newProject.errors.createFailed')));
    } finally {
      setCreating(false);
    }
  };

  const formatGoal = (raw: string, targetCurrency: Currency) => {
    return formatCurrencyInput(raw, targetCurrency);
  };

  const handleGoalAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return;
    const input = e.currentTarget;
    const { value, selectionStart, selectionEnd } = input;
    if (selectionStart !== selectionEnd) return;
    if (currency === 'KRW' && value.startsWith('₩') && selectionStart === 1) {
      e.preventDefault();
      const digits = extractDigits(value).slice(0, -1);
      setGoalAmount(digits ? formatCurrencyInput(digits, 'KRW') : '');
    } else if (currency === 'USD' && value.startsWith('$') && selectionStart === 1) {
      e.preventDefault();
      const digits = extractDigits(value).slice(0, -1);
      setGoalAmount(digits ? formatCurrencyInput(digits, 'USD') : '');
    }
  };

  const handleCurrencyChange = (next: Currency) => {
    if (next === currency) return;
    setCurrency(next);
    setGoalAmount('');
  };

  const openFilePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
    if (typeof picker === 'function') {
      picker.call(input);
      return;
    }
    input.click();
  };

  const handleMediaTypeChange = (nextType: MediaType) => {
    setMediaType(nextType);
    setMediaFile(null);
    setMediaPreviewUrl('');
    if (nextType === 'image') {
      setVideoUrl('');
    }
    // 파일 input 값을 비우지 않으면, 이전에 고른 파일과 동일한 파일을 다시 선택할 때 onChange가 발생하지 않음
    if (mediaInputRef.current) {
      mediaInputRef.current.value = '';
    }
  };

  const handleMediaFileChange = (file: File | null) => {
    if (file && !isImageSizeAllowed(file)) {
      alert(t('common.errors.imageTooLarge'));
      if (mediaInputRef.current) mediaInputRef.current.value = '';
      return;
    }
    setMediaFile(file);
  };

  const requestExitToProjectManagement = () => {
    if (hasDraftChanges) {
      setExitConfirmOpen(true);
      return;
    }
    navigate(PATHS.PROJECT_MANAGEMENT);
  };

  const handleStickyImagePickerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const now = Date.now();
    if (now - lastPickerOpenRef.current < 400) return;
    lastPickerOpenRef.current = now;
    editorRef.current?.openImagePicker();
  };

  const handleStickyImagePickerPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  if (completeOpen) {
    return (
      <PageBackground>
        <CompleteOverlay>
          <CompleteDialog>
            <CompleteLogo src={logoImg} alt="Mission Fund" />
            <CompleteLottieWrap>
              <DotLottieReact src={successCheckLottie} autoplay loop style={{ width: '100%', height: '100%' }} />
            </CompleteLottieWrap>
            <CompleteTitle>
              {(t('page.newProject.complete.title') as string).split('\n').map((line, index, arr) => (
                <React.Fragment key={`complete-title-${index}`}>
                  {line}
                  {index < arr.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </CompleteTitle>
          </CompleteDialog>
            <CompleteButtons>
              <CompleteBtn type="button" onClick={handleShareLink}>
                {t('page.newProject.complete.shareLink')}
              </CompleteBtn>
              <CompleteBtn type="button" onClick={handleOpenPage}>
                {t('page.newProject.complete.openPage')}
              </CompleteBtn>
            </CompleteButtons>
            <CompleteSkip
              type="button"
              onClick={() => {
                setIsShareModalOpen(false);
                setCompleteOpen(false);
                window.location.href = PATHS.PROJECT_MANAGEMENT;
              }}
            >
              {t('page.newProject.complete.skip')}
            </CompleteSkip>
            {isShareModalOpen ? (
              <ShareModalOverlay onPointerDown={() => setIsShareModalOpen(false)}>
                <ShareModalSheet onPointerDown={(e) => e.stopPropagation()}>
                  <ShareModalHeader>
                    <ShareModalTitleWrap>
                      <ShareModalTitle>{t('page.newProject.shareModal.title')}</ShareModalTitle>
                      <ShareModalDesc>
                        {t('common.shareModal.descLine1')}
                        <br />
                        {t('common.shareModal.descLine2')}
                      </ShareModalDesc>
                    </ShareModalTitleWrap>
                    <ShareModalCloseButton
                      type="button"
                      onClick={() => setIsShareModalOpen(false)}
                      aria-label={t('common.actions.close')}
                    >
                      <ShareModalCloseIcon
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        aria-hidden
                        focusable="false"
                      >
                        <path fillRule="evenodd" clipRule="evenodd" d="M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z" fill="#929292" />
                      </ShareModalCloseIcon>
                    </ShareModalCloseButton>
                  </ShareModalHeader>
                  <ShareModalBody>
                    <ShareOption onClick={handleKakaoShare}>
                      <ShareOptionIconKakao>
                        <img src={kakaoIconImg} alt={t('common.shareModal.kakaoAlt')} />
                      </ShareOptionIconKakao>
                      <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
                    </ShareOption>
                    <ShareOption onClick={handleEmailShare}>
                      <ShareOptionIconEmail>
                        <ShareEmailIcon
                          xmlns="http://www.w3.org/2000/svg"
                          width="30"
                          height="24"
                          viewBox="0 0 30 24"
                          fill="none"
                          aria-hidden
                          focusable="false"
                        >
                          <path d="M27 0H3C1.35 0 0.015 1.35 0.015 3L0 21C0 22.65 1.35 24 3 24H27C28.65 24 30 22.65 30 21V3C30 1.35 28.65 0 27 0ZM26.4 6.375L15.795 13.005C15.315 13.305 14.685 13.305 14.205 13.005L3.6 6.375C3.44959 6.29057 3.31788 6.17649 3.21283 6.03968C3.10779 5.90287 3.03159 5.74617 2.98886 5.57906C2.94613 5.41195 2.93775 5.2379 2.96423 5.06746C2.99072 4.89702 3.0515 4.73372 3.14292 4.58745C3.23434 4.44118 3.35449 4.31498 3.49609 4.21649C3.63769 4.118 3.79781 4.04927 3.96674 4.01445C4.13568 3.97963 4.30992 3.97945 4.47893 4.01392C4.64794 4.04839 4.8082 4.1168 4.95 4.215L15 10.5L25.05 4.215C25.1918 4.1168 25.3521 4.04839 25.5211 4.01392C25.6901 3.97945 25.8643 3.97963 26.0333 4.01445C26.2022 4.04927 26.3623 4.118 26.5039 4.21649C26.6455 4.31498 26.7657 4.44118 26.8571 4.58745C26.9485 4.73372 27.0093 4.89702 27.0358 5.06746C27.0622 5.2379 27.0539 5.41195 27.0111 5.57906C26.9684 5.74617 26.8922 5.90287 26.7872 6.03968C26.6821 6.17649 26.5504 6.29057 26.4 6.375Z" fill="#2BC2E1" />
                        </ShareEmailIcon>
                      </ShareOptionIconEmail>
                      <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
                    </ShareOption>
                  </ShareModalBody>
                  <ShareModalFooter>
                    <ShareCopyButton type="button" onClick={handleCopyShareLink}>
                      <ShareCopyIcon
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        aria-hidden
                        focusable="false"
                      >
                        <path d="M16 4H4V16H16V4ZM16 2C17.1 2 18 2.9 18 4V16C18 17.1 17.1 18 16 18H4C2.9 18 2 17.1 2 16V4C2 2.9 2.9 2 4 2H16ZM20 8V20H8V22H20C21.1 22 22 21.1 22 20V8H20Z" fill="#17171B" />
                      </ShareCopyIcon>
                      {t('common.shareModal.copyLink')}
                    </ShareCopyButton>
                  </ShareModalFooter>
                </ShareModalSheet>
              </ShareModalOverlay>
            ) : null}
        </CompleteOverlay>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <DSModal
        open={exitConfirmOpen}
        size="compact"
        title={t('common.exitConfirm.title')}
        description={t('common.exitConfirm.description')}
        onClose={() => setExitConfirmOpen(false)}
        actions={[
          {
            label: t('common.exitConfirm.leave'),
            variant: 'secondary',
            onClick: () => {
              setExitConfirmOpen(false);
              navigate(PATHS.PROJECT_MANAGEMENT);
            },
          },
          { label: t('common.exitConfirm.continue'), variant: 'primary', onClick: () => setExitConfirmOpen(false) },
        ]}
      />
      {showSticky && step !== 3 && !isStoryFocused && !previewOpen ? (
        <TopSticky>
          <TopRow>
            <Stepper>
              {([1, 2, 3, 4, 5] as const).map((n) => (
                <React.Fragment key={n}>
                  <StepDot $active={n === step}>{n}</StepDot>
                  {n !== 5 ? <StepLine /> : null}
                </React.Fragment>
              ))}
            </Stepper>
            <CloseBtn type="button" aria-label="close" onClick={requestExitToProjectManagement}>
              <CloseIcon
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                aria-hidden
                focusable="false"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z"
                  fill="#A6A6A6"
                />
              </CloseIcon>
            </CloseBtn>
          </TopRow>

          <StepHeaderTitle>
            {t(`page.newProject.stepsMobile.step${step}.title`)}
          </StepHeaderTitle>
        </TopSticky>
      ) : null}

      {step === 3 && !previewOpen ? (
        <FocusedTopSticky>
          <FocusedTopRow>
            <FocusedBtn type="button" onClick={() => {
              (document.activeElement as HTMLElement | null)?.blur?.();
              setIsStoryFocused(false);
              setIsKeyboardOpen(false);
            }}>
              {t('common.actions.close')}
            </FocusedBtn>
            <FocusedBtn type="button" onClick={() => {
              (document.activeElement as HTMLElement | null)?.blur?.();
              setIsStoryFocused(false);
              setIsKeyboardOpen(false);
              setStep(4);
            }} $primary>
              {t('common.actions.save')}
            </FocusedBtn>
          </FocusedTopRow>
        </FocusedTopSticky>
      ) : null}

      <Main
        ref={mainRef}
        $topPad={step === 3 ? FOCUSED_TOP_H : 0}
        $bottomPad={step === 3 && isStoryFocused ? KEYBOARD_SCROLL_PAD : 0}
        $storyFocused={step === 3 && isStoryFocused}
      >
        <Content $step={step} $storyFocused={isStoryFocused}>
          {step === 1 ? (
            <Section>
              <Step1Desc>
                {t('page.newProject.steps.step1.desc')}
              </Step1Desc>

              <SectionTitle style={{ marginTop: convertToVwMob(18) }}>{t('page.newProject.sections.fundingType')}</SectionTitle>
              <ChoiceRow>
                <ChoiceCard type="button" $active={fundingType === 'regular'} onClick={() => setFundingType('regular')}>
                  <ChoiceTop $active={fundingType === 'regular'}>{t('page.projectManagement.fundingType.recurring')}</ChoiceTop>
                  <ChoiceSub $active={fundingType === 'regular'}>{t('page.newProject.fundingTypeDescriptions.regular')}</ChoiceSub>
                </ChoiceCard>
                <ChoiceCard type="button" $active={fundingType === 'once'} onClick={() => setFundingType('once')}>
                  <ChoiceTop $active={fundingType === 'once'}>{t('page.projectManagement.fundingType.oneTime')}</ChoiceTop>
                  <ChoiceSub $active={fundingType === 'once'}>{t('page.newProject.fundingTypeDescriptions.once')}</ChoiceSub>
                </ChoiceCard>
              </ChoiceRow>

              <SectionTitle style={{ marginTop: 20 }}>{t('page.projectSettings.sections.goalAmount')}</SectionTitle>
              <RadioRow>
                <RadioBtn type="button" $active={currency === 'KRW'} onClick={() => handleCurrencyChange('KRW')}>
                  <RadioDot $active={currency === 'KRW'} /> {t('common.currency.krw')}
                </RadioBtn>
                <RadioBtn type="button" $active={currency === 'USD'} onClick={() => handleCurrencyChange('USD')}>
                  <RadioDot $active={currency === 'USD'} /> {t('common.currency.usd')}
                </RadioBtn>
              </RadioRow>
              <GoalTextInput
                placeholder={t('page.projectSettings.placeholders.goalAmount')}
                value={goalAmount}
                onChange={(e) => setGoalAmount(formatGoal(e.target.value, currency))}
                onKeyDown={handleGoalAmountKeyDown}
                inputMode="numeric"
                $error={goalAmount.trim() !== '' && numericGoal < (currency === 'KRW' ? 35000 : 35)}
              />
              {showGoalPreview ? <GoalPreview>{goalPreviewText}</GoalPreview> : null}
              <HintBlue>{currency === 'KRW' ? t('page.projectSettings.helpers.minGoal') : t('page.projectSettings.helpers.minGoalUSD')}</HintBlue>

              <SectionTitle style={{ marginTop: 20 }}>{t('page.newProject.sections.projectId')}</SectionTitle>
              <TextInput
                placeholder={t('page.newProject.placeholders.projectId')}
                value={projectId}
                onChange={(e) => setProjectId(sanitizeProjectIdInput(e.target.value))}
                onBlur={handleProjectIdFieldBlur}
                $error={(projectIdTrimmed.length > 0 && !isProjectIdFormatValid) || isProjectIdTaken}
              />
              <HintBlue style={projectIdHintStyle}>
                {projectIdStatusMessage || t('page.newProject.helpers.projectId')}
              </HintBlue>
              <UrlPreview>
                <UrlLabel>{t('page.newProject.labels.urlPreview')}</UrlLabel>
                <UrlValue>
                  https://go.MissionFund.org/
                  <UrlValueHighlight>{projectIdTrimmed || ''}</UrlValueHighlight>
                </UrlValue>
              </UrlPreview>

              <SectionTitle style={{ marginTop: 20 }}>{t('page.projectSettings.sections.projectTitle')}</SectionTitle>
              <TitleTextArea
                placeholder={t('page.projectSettings.placeholders.projectTitle')}
                value={title}
                onChange={(e) => setTitle(sanitizeProjectTitle(e.target.value).slice(0, 40))}
                onFocus={handleTitleFieldFocus}
                $error={titleTrimmed.length > 0 && (titleTrimmed.length < 4 || titleTrimmed.length > 40)}
              />
              <HintBlue>{t('page.projectSettings.helpers.titleLengthShort')}</HintBlue>
            </Section>
          ) : null}

          {step === 2 ? (
            <Section>
              <SectionTitle>{t('page.postManagement.modal.mediaSectionTitle')}</SectionTitle>
              <RadioRow>
                <RadioBtn type="button" $active={mediaType === 'image'} onClick={() => handleMediaTypeChange('image')}>
                  <RadioDot $active={mediaType === 'image'} /> {t('page.postManagement.modal.mediaType.image')}
                </RadioBtn>
                <RadioBtn type="button" $active={mediaType === 'video'} onClick={() => handleMediaTypeChange('video')}>
                  <RadioDot $active={mediaType === 'video'} /> {t('page.postManagement.modal.mediaType.video')}
                </RadioBtn>
              </RadioRow>

              {mediaType === 'video' ? (
                <>
                  <VideoUrlRow>
                    <VideoUrlField
                      $state={showVideoUrlError ? 'error' : videoUrlFocused ? 'focus' : videoUrlTrimmed ? 'complete' : 'default'}
                    >
                      <VideoUrlText>
                        {(videoUrlFocused || videoUrlTrimmed) && <VideoUrlLabel>{t('page.projectSettings.media.youtubeLabel')}</VideoUrlLabel>}
                        <VideoUrlInput
                          placeholder={videoUrlFocused || videoUrlTrimmed ? '' : t('page.projectSettings.media.youtubePlaceholder')}
                          value={videoUrl}
                          onChange={(e) => {
                            setVideoUrl(e.target.value);
                          }}
                          onFocus={() => setVideoUrlFocused(true)}
                          onBlur={() => setVideoUrlFocused(false)}
                        />
                      </VideoUrlText>
                      {videoUrlTrimmed ? (
                        <VideoUrlClear
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setVideoUrl('');
                          }}
                          aria-label="clear"
                        >
                          ×
                        </VideoUrlClear>
                      ) : null}
                    </VideoUrlField>
                  </VideoUrlRow>
                  {showVideoUrlError ? <VideoUrlError>{t('page.projectSettings.media.invalidUrl')}</VideoUrlError> : null}
                </>
              ) : (
                <>
                  <PhotoAttachBtn
                    type="button"
                    onClick={() => openFilePicker(mediaInputRef.current)}
                  >
                    {t('page.postManagement.modal.uploadButton')}
                  </PhotoAttachBtn>
                </>
              )}
              <input
                ref={mediaInputRef}
                type="file"
                accept={mediaType === 'image' ? 'image/*' : 'video/*'}
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
                onChange={(e) => handleMediaFileChange(e.target.files?.[0] ?? null)}
              />

              <MediaPreview $hasMedia={mediaType === 'image' ? Boolean(mediaPreviewUrl) : Boolean(videoPreviewSrc)}>
                {mediaType === 'video' && videoPreviewSrc ? (
                  <PreviewIframe
                    src={videoPreviewSrc}
                    title="video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : mediaType === 'video' && mediaPreviewUrl ? (
                  <PreviewVideo src={mediaPreviewUrl} controls />
                ) : mediaType === 'video' ? (
                  <PreviewEmptyWrap>
                    <PreviewEmpty>
                      {t('page.projectSettings.media.linkPreviewHintMobile', {
                        defaultValue: t('page.projectSettings.media.linkPreviewHint'),
                      })}
                    </PreviewEmpty>
                    <PreviewIconSlot>
                      <PreviewVideoIcon
                        xmlns="http://www.w3.org/2000/svg"
                        width="70"
                        height="70"
                        viewBox="0 0 70 70"
                        fill="none"
                        aria-hidden
                        focusable="false"
                      >
                        <path d="M29.1665 24.7915L46.6665 34.9998L29.1665 45.2082V24.7915Z" fill="#BCBCBC" />
                        <path d="M35 14.5835C61.25 14.5835 61.25 14.5835 61.25 35.0002C61.25 55.4168 61.25 55.4168 35 55.4168C8.75 55.4168 8.75 55.4168 8.75 35.0002C8.75 14.5835 8.75 14.5835 35 14.5835Z" stroke="#BCBCBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </PreviewVideoIcon>
                    </PreviewIconSlot>
                  </PreviewEmptyWrap>
                ) : mediaPreviewUrl ? (
                  <PreviewImg src={mediaPreviewUrl} alt="" />
                ) : (
                  <PreviewEmptyWrap>
                    <PreviewEmpty>
                      {t('page.postManagement.modal.previewHintImageMobile', {
                        defaultValue: t('page.postManagement.modal.previewHintImage'),
                      })}
                    </PreviewEmpty>
                    <PreviewIconSlot>
                      <PreviewIcon src={pictureIconImg} alt="" />
                    </PreviewIconSlot>
                  </PreviewEmptyWrap>
                )}
              </MediaPreview>
            </Section>
          ) : null}

          {step === 3 ? (
            <Section $storyFocused={isStoryFocused}>
              {!isStoryFocused && (
                <>
                  <StoryIntroTitle>{t('page.projectSettings.modal.storyDescLine3')}</StoryIntroTitle>
                </>
              )}

              <RichTextEditor
                ref={editorRef}
                value={storyHtml}
                onChange={setStoryHtml}
                onEnterKey={() => liftStoryScroll(96)}
                onImageInserted={() => {
                  requestAnimationFrame(() => {
                    setIsStoryFocused(true);
                    editorRef.current?.focus();
                    window.setTimeout(() => {
                      editorRef.current?.focus();
                    }, 60);
                    requestAnimationFrame(() => {
                      scrollStep3EditorIntoView();
                      scrollStep3TailIntoView();
                      liftStoryScroll(240);
                    });
                  });
                }}
                editorBottomPaddingPx={isStoryFocused ? STORY_EDITOR_BOTTOM_SPACER_PX : 0}
                placeholder={t('page.projectSettings.modal.storyPlaceholder')}
                pictureIconSrc={pictureIconImg}
                maxImages={3}
                imageCount={totalPhotoCount}
                pinnedImageSrc={isStoryFocused && hasRepresentativeImage ? mediaPreviewUrl : undefined}
                pinnedVideoEmbedUrl={isStoryFocused && mediaType === 'video' && videoPreviewSrc ? videoPreviewSrc : undefined}
                pinnedBadgeLabel={t('common.labels.main')}
                showToolbar={false}
                previewHeightPx={240}
                pinnedInsideEditor
                onFocus={() => {
                  setIsStoryFocused(true);
                  window.requestAnimationFrame(() => {
                    scrollStep3EditorIntoView();
                    scrollStep3TailIntoView();
                    mainRef.current?.scrollBy({ top: 180, behavior: 'auto' });
                  });
                }}
                onBlur={() => undefined}
                onImageUpload={handleEditorImageUpload}
              />

              {!isStoryFocused && (
                <>
                  <TipsTitle>{t('page.newProject.tips.title')}</TipsTitle>
                  <TipsDesc>{t('page.newProject.tips.description')}</TipsDesc>
                  <TipCard>
                    <TipNum>1</TipNum>
                    <TipTextBox>
                      <TipName>{t('page.newProject.tips.items.intro.title')}</TipName>
                      <TipText>{t('page.newProject.tips.items.intro.desc')}</TipText>
                    </TipTextBox>
                  </TipCard>
                  <TipCard>
                    <TipNum>2</TipNum>
                    <TipTextBox>
                      <TipName>{t('page.newProject.tips.items.mission.title')}</TipName>
                      <TipText>{t('page.newProject.tips.items.mission.desc')}</TipText>
                    </TipTextBox>
                  </TipCard>
                  <TipCard>
                    <TipNum>3</TipNum>
                    <TipTextBox>
                      <TipName>{t('page.newProject.tips.items.needs.title')}</TipName>
                      <TipText>{t('page.newProject.tips.items.needs.desc')}</TipText>
                    </TipTextBox>
                  </TipCard>
                  <TipCard>
                    <TipNum>4</TipNum>
                    <TipTextBox>
                      <TipName>{t('page.newProject.tips.items.plan.title')}</TipName>
                      <TipText>{t('page.newProject.tips.items.plan.desc')}</TipText>
                    </TipTextBox>
                  </TipCard>
                  <TipCard>
                    <TipNum>5</TipNum>
                    <TipTextBox>
                      <TipName>{t('page.newProject.tips.items.updates.title')}</TipName>
                      <TipText>{t('page.newProject.tips.items.updates.desc')}</TipText>
                    </TipTextBox>
                  </TipCard>
                </>
              )}
            </Section>
          ) : null}

          {step === 4 ? (
            <Section>
              <MembershipIntro>
                {t('page.newProject.steps.step4.descMob')}
              </MembershipIntro>

              <MembershipCard type="button" $active={membership === 'prime'} onClick={() => setMembership('prime')}>
                <MembershipHeader>
                  <MembershipRadio $active={membership === 'prime'} />
                  <div>
                    <MembershipName $active={membership === 'prime'}>{t('page.newProject.membership.prime.name')}</MembershipName>
                    <MembershipDesc>{t('page.newProject.membership.prime.desc')}</MembershipDesc>
                  </div>
                </MembershipHeader>
                <MembershipChecks>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.prime.benefits.allBuilder')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.prime.benefits.designSupport')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.prime.benefits.kakaoSystem')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.prime.benefits.topExposure')}
                  </MembershipCheckRow>
                </MembershipChecks>
              </MembershipCard>

              <MembershipCard type="button" $active={membership === 'builder'} onClick={() => setMembership('builder')}>
                <MembershipHeader>
                  <MembershipRadio $active={membership === 'builder'} />
                  <div>
                    <MembershipName $active={membership === 'builder'}>{t('page.newProject.membership.builder.name')}</MembershipName>
                    <MembershipDesc>{t('page.newProject.membership.builder.desc')}</MembershipDesc>
                  </div>
                </MembershipHeader>
                <MembershipChecks>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.builder.benefits.allPartner')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.builder.benefits.exposure')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.builder.benefits.prioritySupport')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.builder.benefits.events')}
                  </MembershipCheckRow>
                </MembershipChecks>
              </MembershipCard>

              <MembershipCard type="button" $active={membership === 'partner'} onClick={() => setMembership('partner')}>
                <MembershipHeader>
                  <MembershipRadio $active={membership === 'partner'} />
                  <div>
                    <MembershipName $active={membership === 'partner'}>{t('page.newProject.membership.partner.name')}</MembershipName>
                    <MembershipDesc>{t('page.newProject.membership.partner.desc')}</MembershipDesc>
                  </div>
                </MembershipHeader>
                <MembershipChecks>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.partner.benefits.journal')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.partner.benefits.privateFunding')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.partner.benefits.paymentRecovery')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.partner.benefits.thanksCard')}
                  </MembershipCheckRow>
                </MembershipChecks>
              </MembershipCard>

              <MembershipCard type="button" $active={membership === 'general'} onClick={() => setMembership('general')}>
                <MembershipHeader>
                  <MembershipRadio $active={membership === 'general'} />
                  <div>
                    <MembershipName $active={membership === 'general'}>{t('page.newProject.membership.general.name')}</MembershipName>
                    <MembershipDesc>{t('page.newProject.membership.general.desc')}</MembershipDesc>
                  </div>
                </MembershipHeader>
                <MembershipChecks>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.general.benefits.startFunding')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.general.benefits.basicPage')}
                  </MembershipCheckRow>
                  <MembershipCheckRow>
                    <CheckIcon src={checkIconImg} alt="" /> {formatMembershipBenefitMob('page.newProject.membership.general.benefits.changeable')}
                  </MembershipCheckRow>
                </MembershipChecks>
              </MembershipCard>
            </Section>
          ) : null}

          {step === 5 ? (
            <Section>
              <Step5Desc>{t('page.newProject.steps.step5.desc')}</Step5Desc>

              <Step5Card>
                <Step5CardHeader>
                  <Step5CardTitle>{t('page.newProject.review.basic.title')}</Step5CardTitle>
                  <EditPill type="button" onClick={() => setStep(1)}>
                    {t('page.projectSettings.actions.edit')}
                  </EditPill>
                </Step5CardHeader>

                <Step5Rows>
                  <Step5Row>
                    <Step5Label>{t('page.newProject.review.basic.fundingType')}</Step5Label>
                    <Step5Value>{fundingType === 'regular' ? t('page.newProject.review.fundingType.regular') : t('page.newProject.review.fundingType.once')}</Step5Value>
                  </Step5Row>
                  <Step5Row>
                    <Step5Label>{t('page.projectSettings.labels.goalAmount')}</Step5Label>
                    <Step5Value>
                      {currency === 'KRW' ? formatCurrencyInput(String(numericGoal), 'KRW') : formatCurrencyInput(String(numericGoal), 'USD')}
                    </Step5Value>
                  </Step5Row>
                  <Step5Row>
                    <Step5Label>{t('page.projectSettings.labels.projectTitle')}</Step5Label>
                    <Step5Value>{titleTrimmed || '-'}</Step5Value>
                  </Step5Row>
                  <Step5Row>
                    <Step5Label>{t('page.newProject.labels.projectUrl')}</Step5Label>
                    <Step5UrlValue>{`https://go.MissionFund.org/${projectIdTrimmed || 'yourID'}`}</Step5UrlValue>
                  </Step5Row>
                </Step5Rows>
              </Step5Card>

              <Step5Card>
                <Step5CardHeader>
                  <Step5CardTitle>{t('page.projectSettings.sections.media')}</Step5CardTitle>
                  <EditPill type="button" onClick={() => setStep(2)}>
                    {t('page.projectSettings.actions.edit')}
                  </EditPill>
                </Step5CardHeader>

                <MediaPreview style={{ marginTop: convertToVwMob(12) }}>
                  {mediaType === 'video' && videoPreviewSrc ? (
                    <PreviewIframe
                      src={videoPreviewSrc}
                      title="video preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : mediaType === 'video' && mediaPreviewUrl ? (
                    <PreviewVideo src={mediaPreviewUrl} controls />
                  ) : mediaPreviewUrl ? (
                    <PreviewImg src={mediaPreviewUrl} alt="" />
                  ) : (
                    <PreviewEmptyWrap>
                      <PreviewEmpty>
                        {t('page.postManagement.modal.previewHintImageMobile', {
                          defaultValue: t('page.postManagement.modal.previewHintImage'),
                        })}
                      </PreviewEmpty>
                      <PreviewIcon src={pictureIconImg} alt="" />
                    </PreviewEmptyWrap>
                  )}
                </MediaPreview>
              </Step5Card>

              <Step5Card>
                <Step5CardHeader>
                  <Step5CardTitle>{t('page.projectSettings.sections.story')}</Step5CardTitle>
                  <EditPill type="button" onClick={() => setStep(3)}>
                    {t('page.projectSettings.actions.edit')}
                  </EditPill>
                </Step5CardHeader>

                <Step5StoryBox $expanded={!shouldShowStep5StoryToggle || isStep5StoryExpanded}>
                  {storyHtml ? (
                    <Step5StoryHtml dangerouslySetInnerHTML={{ __html: storyHtml }} />
                  ) : (
                    <Step5Empty>{t('page.newProject.preview.emptyStory')}</Step5Empty>
                  )}
                  {shouldShowStep5StoryToggle ? (
                    <Step5StoryToggleWrap>
                      <Step5StoryToggle type="button" onClick={() => setIsStep5StoryExpanded((prev) => !prev)}>
                        {isStep5StoryExpanded ? t('page.newProject.review.storyToggleCollapse') : t('page.newProject.review.storyToggleExpand')}
                        <Step5StoryToggleIcon
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 13 8"
                          $expanded={isStep5StoryExpanded}
                          aria-hidden
                          focusable="false"
                        >
                          <path
                            d="M1.06022 8.28276e-05L0.000215603 1.06108L5.77722 6.84008C5.86978 6.93324 5.97986 7.00717 6.10111 7.05761C6.22236 7.10806 6.35239 7.13403 6.48372 7.13403C6.61504 7.13403 6.74507 7.10806 6.86632 7.05761C6.98757 7.00717 7.09765 6.93324 7.19021 6.84008L12.9702 1.06108L11.9102 0.00108288L6.48521 5.42508L1.06022 8.28276e-05Z"
                            fill="#676767"
                          />
                        </Step5StoryToggleIcon>
                      </Step5StoryToggle>
                    </Step5StoryToggleWrap>
                  ) : null}
                </Step5StoryBox>
              </Step5Card>

              <Step5Card>
                <Step5CardHeader>
                  <Step5CardTitle>{t('page.newProject.sections.membership')}</Step5CardTitle>
                </Step5CardHeader>
                <Step5MembershipName>
                  {membership === 'partner'
                    ? t('page.newProject.membership.partner.name')
                    : membership === 'builder'
                      ? t('page.newProject.membership.builder.name')
                      : membership === 'prime'
                        ? t('page.newProject.membership.prime.name')
                        : t('page.newProject.membership.general.name')}
                </Step5MembershipName>
                <Step5MembershipDesc>
                  {membership === 'partner'
                    ? t('page.newProject.membership.partner.desc')
                    : membership === 'builder'
                      ? t('page.newProject.membership.builder.desc')
                      : membership === 'prime'
                        ? t('page.newProject.membership.prime.desc')
                        : t('page.newProject.membership.general.desc')}
                </Step5MembershipDesc>
              </Step5Card>
            </Section>
          ) : null}
        </Content>
        {!isStoryFocused && <MobileFooter ref={footerRef} />}
      </Main>

      {!isStoryFocused && step !== 3 ? (
        <BottomSticky>
          {step > 1 ? (
            <BottomBtn type="button" $variant="secondary" onClick={goBack}>
              {t('common.actions.back')}
            </BottomBtn>
          ) : null}
          <BottomBtn
            type="button"
            $variant="primary"
            onClick={step === 5 ? () => setPreviewOpen(true) : goNext}
            disabled={!canNext || (step === 5 && creating)}
          >
            {step === 5 ? t('common.actions.confirm') : t('common.actions.next')}
          </BottomBtn>
        </BottomSticky>
      ) : null}

      {isStoryFocused ? (
        <KeyboardSticky ref={keyboardStickyRef}>
          <KeyboardAddPhotoBtn
            type="button"
            onPointerDown={handleStickyImagePickerPointerDown}
            onClick={handleStickyImagePickerClick}
            disabled={totalPhotoCount >= 3}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M23.7183 20.5998C23.8467 20.1728 23.9167 19.7189 23.9167 19.2499V8.74992C23.9167 7.51224 23.425 6.32526 22.5498 5.45009C21.6747 4.57492 20.4877 4.08325 19.25 4.08325H8.75C7.51232 4.08325 6.32534 4.57492 5.45017 5.45009C4.575 6.32526 4.08333 7.51224 4.08333 8.74992V19.3316C4.10475 20.5551 4.60585 21.7212 5.47871 22.5787C6.35157 23.4363 7.52634 23.9168 8.75 23.9166H19.25L19.3865 23.9143M23.7183 20.5998L23.6157 20.4784L20.7387 17.0064C20.5204 16.743 20.2468 16.5309 19.9374 16.385C19.628 16.2391 19.2903 16.163 18.9483 16.1621C18.6062 16.1612 18.2681 16.2355 17.958 16.3798C17.6478 16.5241 17.3732 16.7349 17.1535 16.9971L15.6228 18.8241L15.3732 19.1286M23.7183 20.5998C23.4353 21.537 22.8647 22.3602 22.0873 22.9553C21.3099 23.5503 20.3651 23.8858 19.3865 23.9143M15.3732 19.1286L19.278 23.7906L19.3865 23.9143M15.3732 19.1286L11.6083 14.6334C11.3893 14.372 11.1157 14.1618 10.8067 14.0176C10.4977 13.8734 10.1608 13.7987 9.81983 13.7987C9.47883 13.7987 9.14198 13.8734 8.83298 14.0176C8.52397 14.1618 8.25034 14.372 8.03133 14.6334L4.291 19.0983L4.0845 19.3328" stroke="#676767" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17.6062 12.1438C18.5727 12.1438 19.3562 11.3603 19.3562 10.3938C19.3562 9.4273 18.5727 8.6438 17.6062 8.6438C16.6397 8.6438 15.8562 9.4273 15.8562 10.3938C15.8562 11.3603 16.6397 12.1438 17.6062 12.1438Z" fill="#676767" />
            </svg>
            {t('page.postManagement.modal.addPhoto', { count: totalPhotoCount })}
          </KeyboardAddPhotoBtn>
        </KeyboardSticky>
      ) : null}

      {/* MobileFooter는 이제 스크롤 영역 밖에 있으므로 항상 보이지 않을 수 있습니다. 
          원래 의도는 Main 스크롤 끝에 Footer가 있는 것이므로, Main 안으로 옮겨야 합니다. 
          하지만 ref={footerRef}가 IntersectionObserver에 쓰이므로, 
          Main 안에 MobileFooter를 렌더링해야 스크롤 시 감지됩니다. */}
      {/* 기존 코드에서는 PageBackground 바로 자식이었는데, 
          PageBackground가 flex column이므로 Main 다음 요소로 렌더링되면 화면 하단에 고정되지 않고
          BottomSticky 위에 배치되거나, Main이 flex:1이라서 화면 밖으로 밀려날 수 있습니다.
          
          하지만 MobileFooter는 "페이지의 맨 끝"을 의미하는 요소로 보입니다.
          TopSticky/BottomSticky가 있을 때는 가려지더라도, 스크롤 끝까지 내리면 보여야 하는 요소인가요?
          기존 코드: <MobileFooter ref={footerRef} /> 가 <Main> 뒤에 있었습니다.
          PageBackground는 원래 block 요소였으므로 Main 아래에 배치되어 스크롤 끝에 보였을 것입니다.
          
          이제 PageBackground가 flex column, h-100dvh, overflow-hidden입니다.
          Main은 flex:1, overflow-y:auto 입니다.
          MobileFooter를 Main **내부**의 맨 끝에 넣어야 스크롤 시 하단에 보입니다.
      */}
      
      {/* MobileFooter를 Main 내부로 이동시키지 않고 여기서 렌더링하면,
          Main이 flex:1을 차지하고 그 아래에 MobileFooter가 붙습니다.
          그리고 BottomSticky도 그 아래에 붙습니다.
          
          문제: Main이 overflow-y:auto이므로 Main 영역 안에서만 스크롤이 발생합니다.
          MobileFooter가 Main 밖에 있으면, Main 스크롤과는 무관하게 항상 화면 하단(BottomSticky 위)에 고정되어 보이게 됩니다.
          
          원래 의도: MobileFooter는 컨텐츠의 끝에 위치해야 함.
          따라서 MobileFooter를 Main 컴포넌트 내부로 이동시켜야 합니다.
          하지만 Main은 styled-component라서 children으로 전달해야 합니다.
      */}

      {previewOpen ? (
        <PreviewOverlay
          onPointerDown={() => {
            setPreviewOpen(false);
          }}
        >
          {membership === 'general' ? (
            <PreviewGeneralMob
              title={titleTrimmed}
              projectId={projectIdTrimmed}
              goalAmount={goalAmount}
              currency={currency}
              mediaPreviewUrl={mediaPreviewUrl}
              storyHtml={storyHtml}
              userFullName={userFullName}
              onClose={() => setPreviewOpen(false)}
              onCreate={handleCreate}
              creating={creating}
              creatingLabel={creatingLabel}
              videoPreviewSrc={videoPreviewSrc}
            />
          ) : (
            <PreviewPartnerMob
              title={titleTrimmed}
              projectId={projectIdTrimmed}
              goalAmount={goalAmount}
              currency={currency}
              mediaPreviewUrl={mediaPreviewUrl}
              storyHtml={storyHtml}
              userFullName={userFullName}
              membership={membership}
              onClose={() => setPreviewOpen(false)}
              onCreate={handleCreate}
              creating={creating}
              creatingLabel={creatingLabel}
              videoPreviewSrc={videoPreviewSrc}
            />
          )}
        </PreviewOverlay>
      ) : null}
    </PageBackground>
  );
};

export default NewProjectMob;

const PageBackground = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  background: ${({ theme }) => theme.colors.grayScale[6]};
  overflow: hidden;
`;

const FocusedTopSticky = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1003;
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  padding: ${convertToVwMob(16)} ${convertToVwMob(20)};
`;

const FocusedTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FocusedBtn = styled.button<{ $primary?: boolean }>`
  border: none;
  background: transparent;
  color: ${({ $primary }) => ($primary ? '#2BC2E1' : '#17171b')};
  ${({ theme }) => theme.typography.contents.medium20}
  font-size: ${convertToVwMob(20)};
  cursor: pointer;
  padding: 0;
`;

const TopSticky = styled.header`
  position: relative;
  flex: 0 0 auto;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-bottom: 1px solid #eee;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${convertToVwMob(16)};
`;

const StepHeaderTitle = styled.div`
  margin-top: ${convertToVwMob(12)};
  color: #000;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const CloseIcon = styled.svg`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  display: block;
  margin-left : ${convertToVwMob(30)};
`;

const Stepper = styled.div`
  margin-top: ${convertToVwMob(10)};
  display: flex;
  align-items: center;
  width : 90%;
`;

const StepDot = styled.div<{ $active: boolean }>`
  display: flex;
  width: ${({ $active }) => ($active ? convertToVwMob(24) : convertToVwMob(18))};
  height: ${({ $active }) => ($active ? convertToVwMob(24) : convertToVwMob(18))};
  border-radius: 999px;
  align-items: center;
  justify-content: center;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 700;
  color: #fff;
  background: ${({ $active }) => ($active ? '#2BC2E1' : '#DFDFDF')};
  flex: 0 0 auto;
`;

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  min-width: ${convertToVwMob(18)};
  background: #dfdfdf;
`;

const Main = styled.main<{ $topPad: number; $bottomPad: number; $storyFocused?: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding-top: ${({ $topPad }) => ($topPad ? convertToVwMob($topPad) : 0)};
  padding-bottom: ${({ $bottomPad }) => ($bottomPad ? convertToVwMob($bottomPad) : 0)};
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: ${({ $storyFocused }) => ($storyFocused ? '#ffffff' : 'transparent')};

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Content = styled.div<{ $step: 1 | 2 | 3 | 4 | 5; $storyFocused: boolean }>`
  padding: ${convertToVwMob(20)} ${convertToVwMob(20)} 0;
  padding-bottom: ${({ $step }) => ($step === 3 ? '0px' : convertToVwMob(180))};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(14)};
`;

const Section = styled.section<{ $storyFocused?: boolean }>`
  background: ${({ $storyFocused }) => ($storyFocused ? '#ffffff' : '#f5f6f7')};
  border: none;
  border-radius: 0;
  padding: 0;
`;

const Step1Desc = styled.div`
  margin-top: ${convertToVwMob(14)};
  color: #000;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const SectionTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
`;

const StoryIntroTitle = styled(SectionTitle)`
  white-space: pre-line;
`;

const ChoiceRow = styled.div`
  margin-top: ${convertToVwMob(12)};
  display: grid;
  grid-template-columns: 1fr;
  gap: ${convertToVwMob(10)};
`;

const ChoiceCard = styled.button<{ $active: boolean }>`
  text-align: left;
  padding: ${convertToVwMob(12)};
  border-radius: ${convertToVwMob(12)};
  border: ${({ $active }) => ($active ? '2.4px solid #2BC2E1' : '1px solid #EEE')};
  background: ${({ $active }) => ($active ? '#EBFCFF' : '#fff')};
  cursor: pointer;
`;

const ChoiceTop = styled.div<{ $active: boolean }>`
  color: ${({ $active }) => ($active ? '#17171B' : '#676767')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(22)};
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
`;

const ChoiceSub = styled.div<{ $active: boolean }>`
  margin-top: ${convertToVwMob(4)};
  color: ${({ $active }) => ($active ? '#676767' : '#BCBCBC')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(18)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const RadioRow = styled.div`
  margin-top: ${convertToVwMob(10)};
  display: flex;
  gap: ${convertToVwMob(12)};
  flex-wrap: wrap;
`;

const RadioBtn = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  cursor: pointer;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  opacity: ${({ $active }) => ($active ? 1 : 0.85)};
`;

const RadioDot = styled.span<{ $active: boolean }>`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  border-radius: 999px;
  border: ${convertToVwMob(1.5)} solid #E5E5E5;
  background: #ffffff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(10)};
    height: ${convertToVwMob(10)};
    border-radius: 50%;
    background: #2bc2e1;
    transform: ${({ $active }) =>
      $active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const TextInput = styled.input<{ $error?: boolean }>`
  margin-top: ${convertToVwMob(10)};
  width: 100%;
  height: ${convertToVwMob(44)};
  border-radius: ${convertToVwMob(6)};
  border: 1px solid ${({ $error }) => ($error ? theme.colors.other.red10 : '#e5e5e5')};
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  color: #17171b;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ $error }) => ($error ? theme.colors.other.red10 : '#2bc2e1')};
  }

  &:focus {
    border-color: ${({ $error }) => ($error ? theme.colors.other.red10 : '#2bc2e1')};
    box-shadow: ${({ $error }) =>
      $error ? `0 0 0 2px ${theme.colors.other.red10}40` : '0 0 0 2px rgba(43, 194, 225, 0.2)'};
    outline: none;
  }
`;

const GoalTextInput = styled(TextInput)`
  height: auto;
  min-height: ${convertToVwMob(56)};
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ $error }) => ($error ? theme.colors.other.red10 : '#e5e5e5')};
  border-radius: 0;
  padding: 0 0 ${convertToVwMob(2)};
  font-size: ${convertToVwMob(32)};
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  color: ${({ $error }) => ($error ? theme.colors.other.red10 : '#17171B')};

  &::placeholder {
    color: #929292;
  }

  &:hover {
    border-bottom-color: ${({ $error }) => ($error ? theme.colors.other.red10 : '#2bc2e1')};
  }

  &:focus {
    border-bottom-color: ${({ $error }) => ($error ? theme.colors.other.red10 : '#2bc2e1')};
    box-shadow: none;
  }
`;

const GoalPreview = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: #404040;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  white-space: normal;
  word-break: keep-all;
`;

const TitleTextArea = styled.textarea<{ $error?: boolean }>`
  margin-top: ${convertToVwMob(10)};
  width: 100%;
  height: ${convertToVwMob(88)};
  border-radius: ${convertToVwMob(6)};
  border: 1px solid ${({ $error }) => ($error ? theme.colors.other.red10 : '#e5e5e5')};
  padding: ${convertToVwMob(12)};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  color: #17171b;
  line-height: 150%;
  resize: none;
  word-break: keep-all;
  overflow-wrap: break-word;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ $error }) => ($error ? theme.colors.other.red10 : '#2bc2e1')};
  }

  &:focus {
    border-color: ${({ $error }) => ($error ? theme.colors.other.red10 : '#2bc2e1')};
    box-shadow: ${({ $error }) =>
      $error ? `0 0 0 2px ${theme.colors.other.red10}40` : '0 0 0 2px rgba(43, 194, 225, 0.2)'};
    outline: none;
  }
`;

const HintBlue = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: #357b89;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
`;

const UrlPreview = styled.div`
  margin-top: ${convertToVwMob(10)};
  border-radius: ${convertToVwMob(8)};
  background: #f0f1f2;
  padding: ${convertToVwMob(12)};
`;

const UrlLabel = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
`;


const UrlValue = styled.div`
  margin-top: ${convertToVwMob(4)};
  color: #17171b;
  font-family: "Roboto Mono";
  font-size: ${convertToVwMob(16)};
  font-style: italic;
  font-weight: 400;
  line-height: ${convertToVwMob(15)};
  word-break: break-word;
`;

const UrlValueHighlight = styled.span`
  color: #026478;
  font-family: "Roboto Mono";
  font-size: ${convertToVwMob(12)};
  font-style: italic;
  font-weight: 700;
  line-height: ${convertToVwMob(15)};
`;

const PhotoAttachBtn = styled.button`
  margin-top: ${convertToVwMob(12)};
  width: 100%;
  height: ${convertToVwMob(52)};
  border-radius: ${convertToVwMob(12)};
  border: 2.4px solid #2bc2e1;
  background: #ebfcff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: ${convertToVwMob(18)};
  font-weight: 700;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
`;

const MediaPreview = styled.div<{ $hasMedia?: boolean }>`
  margin-top: ${convertToVwMob(12)};
  height: ${convertToVwMob(220)};
  border-radius: ${convertToVwMob(10)};
  border: ${({ $hasMedia }) => ($hasMedia ? '3px solid #2BC2E1' : '1px solid #e5e5e5')};
  background: #f0f1f2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const PreviewEmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(10)};
  text-align: center;
`;

const PreviewEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  white-space: pre-line;
  min-height: ${convertToVwMob(42)};
`;

const PreviewIcon = styled.img`
  width: ${convertToVwMob(48)};
  height: ${convertToVwMob(48)};
  object-fit: contain;
`;

const PreviewIconSlot = styled.div`
  height: ${convertToVwMob(70)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewVideoIcon = styled.svg`
  width: ${convertToVwMob(70)};
  height: ${convertToVwMob(70)};
`;


const VideoUrlRow = styled.div`
  margin-top: ${convertToVwMob(12)};
  display: flex;
  align-items: stretch;
  gap: ${convertToVwMob(10)};
`;

const VideoUrlField = styled.div<{ $state: 'default' | 'focus' | 'complete' | 'error' }>`
  flex: 1;
  border-radius: ${convertToVwMob(6)};
  border: ${convertToVwMob(2)} solid
    ${({ $state }) =>
      $state === 'error' ? '#ff0f0f' : $state === 'focus' ? '#2bc2e1' : '#dfdfdf'};
  background: #ffffff;
  padding: ${convertToVwMob(10)} ${convertToVwMob(12)};
  min-height: ${convertToVwMob(52)};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
`;

const VideoUrlText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
  min-width: 0;
`;

const VideoUrlLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 400;
`;

const VideoUrlInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 700;
  color: #17171b;
  background: transparent;

  &::placeholder {
    color: #bcbcbc;
    font-weight: 400;
  }
`;

const VideoUrlClear = styled.button`
  width: ${convertToVwMob(28)};
  height: ${convertToVwMob(28)};
  border-radius: 999px;
  border: none;
  background: #bcbcbc;
  color: #ffffff;
  font-size: ${convertToVwMob(18)};
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
`;

const VideoUrlError = styled.div`
  margin-top: ${convertToVwMob(6)};
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 400;
`;

const VideoAttachBtn = styled.button`
  height: ${convertToVwMob(56)};
  width: ${convertToVwMob(96)};
  border-radius: ${convertToVwMob(12)};
  border: 1px solid #dfdfdf;
  background: #ffffff;
  color: #404040;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;



const TipsTitle = styled.div`
  margin-top: ${convertToVwMob(24)};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 700;
`;

const TipsDesc = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
`;

const TipCard = styled.div`
  margin-top: ${convertToVwMob(10)};
  border: 1px solid #eee;
  border-radius: ${convertToVwMob(10)};
  background: #fff;
  padding: ${convertToVwMob(12)};
  display: flex;
  gap: ${convertToVwMob(10)};
  align-items: flex-start;
`;

const TipNum = styled.div`
  width: ${convertToVwMob(22)};
  height: ${convertToVwMob(22)};
  border-radius: 999px;
  background: #357b89;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 700;
  flex: 0 0 auto;
`;

const TipTextBox = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TipName = styled.div`
  color: #111827;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
`;

const TipText = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
`;

const MembershipIntro = styled.div`
  white-space: pre-line;
  color: #000;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: ${convertToVwMob(14)};
`;

const MembershipCard = styled.button<{ $active: boolean }>`
  margin-top: ${convertToVwMob(12)};
  text-align: left;
  border-radius: ${convertToVwMob(10)};
  border: ${({ $active }) => ($active ? '2.4px solid #2BC2E1' : '1px solid #EEE')};
  background: ${({ $active }) => ($active ? '#EBFCFF' : '#fff')};
  color: ${({ $active }) => ($active ? '#17171B' : '#676767')};
  padding: ${convertToVwMob(14)};
  cursor: pointer;
  width: 100%;
`;

const MembershipHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${convertToVwMob(12)};
`;

const MembershipRadio = styled.span<{ $active: boolean }>`
  margin-top: ${convertToVwMob(2)};
  width: ${convertToVwMob(22)};
  height: ${convertToVwMob(22)};
  border-radius: 999px;
  border: ${convertToVwMob(1.5)} solid #E5E5E5;
  background: #fff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(11)};
    height: ${convertToVwMob(11)};
    border-radius: 50%;
    background: #2BC2E1;
    transform: ${({ $active }) =>
      $active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const MembershipName = styled.div<{ $active: boolean }>`
  color: ${({ $active }) => ($active ? '#17171B' : '#676767')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const MembershipDesc = styled.div`
  margin-top: ${convertToVwMob(8)};
  white-space: pre-line;
  color: inherit;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const MembershipChecks = styled.div`
  margin-top: ${convertToVwMob(14)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(10)};
  padding-left: ${convertToVwMob(34)};
`;

const MembershipCheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${convertToVwMob(10)};
  color: inherit;
  white-space: pre-line;
  word-break: keep-all;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const CheckIcon = styled.img`
  width: ${convertToVwMob(18)};
  height: ${convertToVwMob(18)};
  object-fit: contain;
  flex: 0 0 auto;
`;


const Step5Desc = styled.div`
  margin-top: ${convertToVwMob(10)};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  white-space: pre-line;
  margin-bottom : ${convertToVwMob(20)};
`;

const Step5Card = styled.div`
  border: 1px solid #eee;
  border-radius: ${convertToVwMob(0)};
  background: #fff;
  padding: ${convertToVwMob(14)};
`;

const Step5CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${convertToVwMob(12)};
`;

const Step5CardTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const EditPill = styled.button`
  height: ${convertToVwMob(40)};
  padding: 0 ${convertToVwMob(16)};
  border-radius: 100px;
  border: 1px solid #82e8ff;
  background: #fff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
`;

const Step5Rows = styled.div`
  margin-top: ${convertToVwMob(14)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
`;

const Step5Row = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${convertToVwMob(6)};
`;

const Step5Label = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const Step5Value = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  word-break: keep-all;
  overflow-wrap: break-word;
`;

const Step5UrlValue = styled(Step5Value)`
  word-break: break-all;
`;


const Step5StoryHtml = styled.div`
  color: var(--GreyScale-70, #676767);
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  white-space: pre-wrap;

  button[data-embed='delete-btn'] {
    display: none !important;
  }

  figure[data-embed='pinned-image'] {
    margin: 0 0 ${convertToVwMob(12)};
    border-radius: ${convertToVwMob(10)};
    overflow: hidden;
    border: 2px solid #2bc2e1;
    background: #f0f1f2;
    width: 100%;
    box-sizing: border-box;
  }

  figure[data-embed='story-image'] {
    margin: ${convertToVwMob(12)} 0;
    border-radius: ${convertToVwMob(10)};
    overflow: hidden;
    border: 2px solid #2bc2e1;
    background: #f0f1f2;
    width: 100%;
    box-sizing: border-box;
  }

  figure[data-embed='pinned-image'] img[data-embed='image'],
  figure[data-embed='story-image'] > img[data-embed='image'] {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }
`;

const Step5StoryBox = styled.div<{ $expanded: boolean }>`
  margin-top: ${convertToVwMob(12)};
  padding: 0;

  ${({ $expanded }) =>
    !$expanded &&
    `
    ${Step5StoryHtml} {
      max-height: ${convertToVwMob(280)};
      overflow: hidden;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: ${convertToVwMob(60)};
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
        pointer-events: none;
      }
    }
  `}
`;

const Step5Empty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const Step5StoryToggle = styled.button`
  margin: ${convertToVwMob(14)} auto 0;
  height: ${convertToVwMob(44)};
  padding: 0 ${convertToVwMob(18)};
  border-radius: 999px;
  border: 1px solid #bcbcbc;
  background: #fff;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  cursor: pointer;
`;

const Step5StoryToggleWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Step5StoryToggleIcon = styled.svg<{ $expanded: boolean }>`
  width: ${convertToVwMob(13)};
  height: ${convertToVwMob(8)};
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`;

const Step5MembershipName = styled.div`
  margin-top: ${convertToVwMob(12)};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const Step5MembershipDesc = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const BottomSticky = styled.div`
  position: relative;
  flex: 0 0 auto;
  z-index: 1000;
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid #eee;
  padding: ${convertToVwMob(16)} ${convertToVwMob(32)} ${convertToVwMob(32)};
  display: flex;
  justify-content: flex-end;
  gap: ${convertToVwMob(8)};
  touch-action: none;
`;

const KeyboardSticky = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  flex: 0 0 auto;
  z-index: 1002;
  background: #fdfdfd;
  border-top: 1px solid #bcbcbc;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  padding: ${convertToVwMob(8)};
  padding-bottom: calc(${convertToVwMob(8)} + env(safe-area-inset-bottom));
`;

const KeyboardAddPhotoBtn = styled.button`
  width: 100%;
  height: ${convertToVwMob(46)};
  border-radius: ${convertToVwMob(12)};
  border: none;
  background: #ffffff;
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  padding-bottom: ${convertToVwMob(0)};

  font-weight: 400;
  line-height: 130%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(10)};
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
`;

const BottomBtn = styled.button<{ $variant: 'primary' | 'secondary' }>`
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  border-radius: ${convertToVwMob(4)};
  border: ${({ $variant }) => ($variant === 'secondary' ? '1px solid #DFDFDF' : 'none')};
  background: ${({ $variant, disabled }) => (disabled ? '#DFDFDF' : $variant === 'primary' ? '#026478' : '#FFFFFF')};
  color: ${({ $variant, disabled }) => (disabled ? '#BCBCBC' : $variant === 'primary' ? '#FFFFFF' : '#676767')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Preview Styles
const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  background: #f0f1f2;
`;

// Complete Overlay Styles
const CompleteOverlay = styled.div`
  position: fixed;
  inset: 0;
  height : 100vh;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${convertToVwMob(24)};
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(80px);
  -webkit-backdrop-filter: blur(80px);
  overflow: hidden;
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: -20%;
    background: linear-gradient(
      90deg,
      rgba(43, 194, 225, 0.55) 0%,
      rgba(255, 255, 255, 0.55) 45%,
      rgba(255, 223, 166, 0.55) 100%
    );
    background-size: 200% 200%;
    animation: completeBgMove 4s ease-in-out infinite alternate;
    filter: blur(80px);
    opacity: 1;
    z-index: 0;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.02);
    z-index: 0;
    pointer-events: none;
  }

  @keyframes completeBgMove {
    0% {
      background-position: 0% 50%;
      transform: translate3d(-2%, 0, 0);
    }
    100% {
      background-position: 100% 50%;
      transform: translate3d(2%, 0, 0);
    }
  }
`;

const CompleteDialog = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  border-radius: ${convertToVwMob(40)};
  background: #fff;
  background-clip: padding-box;
  isolation: isolate;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  padding: ${convertToVwMob(60)} ${convertToVwMob(32)} ${convertToVwMob(40)};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const CompleteLogo = styled.img`
  height: ${convertToVwMob(44)};
  object-fit: contain;
  margin-bottom: ${convertToVwMob(24)};
`;

const CompleteLottieWrap = styled.div`
  width: ${convertToVwMob(120)};
  height: ${convertToVwMob(120)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${convertToVwMob(20)};
`;

const CompleteTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(32)};
  font-weight: 400;
  line-height: 1.3;
  color: var(--grey-scale-90-main-text, #17171B);
`;

const CompleteButtons = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  margin-bottom: ${convertToVwMob(32)};
  margin-top: ${convertToVwMob(24)};
  position: relative;
  z-index: 2;
`;

const CompleteBtn = styled.button`
  width: 100%;
  height: ${convertToVwMob(60)};
  border-radius: 999px;
  border: 1.4px solid var(--primary-blue-40, #2BC2E1);
  background: #fff;
  color: #2BC2E1;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:active {
    background: #B9F3FF;
  }
`;

const CompleteSkip = styled.button`
  background: transparent;
  border: none;
  color: var(--GreyScale-70, #676767);
  text-align: center;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
  cursor: pointer;
`;

const ShareModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const ShareModalSheet = styled.div`
  width: 100%;
  border-radius: ${convertToVwMob(40)} ${convertToVwMob(40)} 0 0;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
`;

const ShareModalHeader = styled.div`
  padding: ${convertToVwMob(32)} ${convertToVwMob(30)} ${convertToVwMob(20)};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const ShareModalTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
`;

const ShareModalTitle = styled.h2`
  margin: 0;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 130%;
`;

const ShareModalDesc = styled.p`
  margin: 0;
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const ShareModalCloseButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ShareModalCloseIcon = styled.svg`
  width: 100%;
  height: 100%;
`;

const ShareModalBody = styled.div`
  padding: 0 ${convertToVwMob(32)} ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
`;

const ShareOption = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(20)};
  cursor: pointer;
`;

const ShareOptionIconKakao = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  border-radius: 999px;
  background: #f9e000;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: ${convertToVwMob(34)};
    height: ${convertToVwMob(34)};
    object-fit: contain;
  }
`;

const ShareOptionIconEmail = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  border-radius: 999px;
  background: #ebfcff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ShareEmailIcon = styled.svg`
  width: ${convertToVwMob(30)};
  height: ${convertToVwMob(24)};
  flex: 0 0 auto;
`;

const ShareOptionText = styled.span`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const ShareModalFooter = styled.div`
  background: #f8f8f8;
  padding: ${convertToVwMob(20)} ${convertToVwMob(30)};
  padding-bottom: calc(${convertToVwMob(24)} + env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ShareCopyButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(64)};
  border-radius: ${convertToVwMob(14)};
  border: 1px solid #bcbcbc;
  background: #fff;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(10)};
  cursor: pointer;
`;

const ShareCopyIcon = styled.svg`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  flex: 0 0 auto;
`;
